import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from '@carbon/react';

import { FullPageWizard } from './full-page-wizard';
import { FullPageWizardLayoutProps } from './types';
import styles from './full-page-wizard-layout.module.scss';

/**
 * Full-page layout wrapper that renders breadcrumbs and a title above the
 * FullPageWizard stepper. Fills the viewport below the app shell header.
 *
 * The title row is a flex row so a caller can put controls beside the heading
 * (`titleAdornment`) without stealing a line from the step content below — the
 * header has intrinsic height inside a fixed-height column, so anything that
 * adds a row takes it off the step.
 *
 * @param {FullPageWizardLayoutProps} props - Breadcrumbs, title, steps, and onCancel
 * @return {JSX.Element} The full-page wizard layout
 */
const FullPageWizardLayout: FC<FullPageWizardLayoutProps> = ({
  breadcrumbs,
  title,
  breadcrumbLabel,
  titleAdornment,
  steps,
  onCancel,
}) => (
  <div className={styles.layout}>
    <header className={styles.header}>
      {breadcrumbs.length > 0 && (
        <div className={styles.breadcrumbs}>
          <Breadcrumb noTrailingSlash>
            {breadcrumbs.map(([path, label]) => (
              <BreadcrumbItem key={path}>
                <NavLink to={path}>{label}</NavLink>
              </BreadcrumbItem>
            ))}
            <BreadcrumbItem isCurrentPage>
              {breadcrumbLabel ?? title}
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
      )}
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{title}</h1>
        {titleAdornment}
      </div>
    </header>
    <div className={styles.wizardContainer}>
      <FullPageWizard steps={steps} onCancel={onCancel} />
    </div>
  </div>
);

export { FullPageWizardLayout };
